import React, { useEffect, useRef } from 'react';
import 'aframe';
import './components/cubemap';
import './controls/OrbitControls';
import PanoControlsV2 from './controls/PanoControls/PanoControlsV2';
import PlaceholderLight from '../../images/placeholder-light-logo.png';
import './AFramePanoramaV2.scss';
import { getCubeSrc } from "../../utils/panoUtils";

const AFramePanoramaV2 = ({
  id,
  pano,
  primaryColor = '#009b65',
  onCameraUpdate,
  onMouseDown,
  setAutoRotate,
}) => {
  const {
    url,
    alt='',
    initialView,
    autoRotate=false,
    autoRotateDirection='counterclockwise'
  } = pano;

  const defaultCameraView = {
    position: { x: 0, y: 0, z: -0.2 },
    rotation: { x: -8.414080508010864, y: 0, z: 0 },
  };

  const cubeSrc = getCubeSrc(url);

  const cubeRef = useRef();
  const cameraRef = useRef();
  const sceneRef = useRef();

  const onCameraChanged = () => {
    if (onCameraUpdate && cameraRef.current) {
      const rotation = cameraRef.current.getAttribute('rotation');
      const position = cameraRef.current.getAttribute('position');

      onCameraUpdate({ rotation, position });
    }
  }

  const initCameraView = () => {
    if (cameraRef.current) {
      const initialPosition = initialView?.position || defaultCameraView.position;
      const initialRotation = initialView?.rotation || defaultCameraView.rotation;
      cameraRef.current.setAttribute('position', initialPosition);
      cameraRef.current.setAttribute('rotation', initialRotation);
    }
  }

  useEffect(() => {
    initCameraView();

    const camera = cameraRef.current;
    if (camera && onCameraUpdate) {
      camera.addEventListener('componentchanged', onCameraChanged);
    }

    return () => {
      if (camera && onCameraUpdate) {
        camera.removeEventListener('componentchanged', onCameraChanged);
      }
    }
  }, []);

  useEffect(() => {
    // stop auto rotate when user interacts with the scene
    const handleUserInteraction = () => {
      autoRotate && setAutoRotate(false);
    }

    if (sceneRef.current) {
      sceneRef.current.addEventListener('user-interaction-detected', handleUserInteraction);
    }
    return () => {
      sceneRef.current?.removeEventListener('user-interaction-detected', handleUserInteraction);
    };
  }, [autoRotate, setAutoRotate]);

  if (!cubeSrc) return null;

  return (
    <a-scene
      id={id}
      class="aframe-panorama-v2"
      raycaster="objects: .clickable;"
      cursor="rayOrigin: mouse;"
      vr-mode-ui="enabled: false"
      device-orientation-permission-ui="enabled: false"
      loading-screen="enabled: false"
      aria-label={`Panorama${alt ? ` - ${alt}` : ''}. Drag with your mouse or use the arrow keys to rotate the view.`}
      role="application"
      tabIndex={0}
      embedded
      style={{
        background: `url(${PlaceholderLight}) no-repeat center center`,
      }}
      onMouseDown={onMouseDown}
      ref={sceneRef}
    >
      <PanoControlsV2
        autoRotate={autoRotate}
        setAutoRotate={setAutoRotate}
        autoRotateDirection={autoRotateDirection}
        primaryColor={primaryColor}
      />

      <a-entity
        id="sky"
        ref={cubeRef}
        cubemap={`
          folder: ${cubeSrc};
          urls: pano_r, pano_l, pano_u, pano_d, pano_f, pano_b;
          edgeLength: 5000;
          transparent: false;
          opacity: 1;
        `}
        animation__fadein="
          property: cubemap.opacity;
          to: 1;
          dur: 500;
          startEvents: srcLoaded;
        "
      />
      <a-camera
        id="camera"
        ref={cameraRef}
        wasd-controls-enabled="false"
        look-controls-enabled="false"
        orbit-controls={`
          autoRotate: ${!!autoRotate};
          keyRotateSpeed: 1;
          autoRotateSpeed: ${autoRotateDirection === 'clockwise' ? 0.05 : -0.05};
          target: #sky;
          enableDamping: true;
          enableZoom: true;
          enableKeys: true;
          dampingFactor: 0.11;
          rotateSpeed: -0.14;
          touchRotateSpeed: -0.05;
          minZoom: 0.75;
          maxZoom: 2;
          initialZoom: 0.75;
          minDistance: 0.2;
          maxDistance: 0.2;
          autoVRLookCam: true;
        `}
      />
    </a-scene>
  );
};

export default AFramePanoramaV2;
